import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import { EmptyState, LoadingBox } from "@/components/ui";
import { theme } from "@/theme/theme";
import { BrowserSessionStatusChip } from "../components/browser-session-status-chip";
import { HarViewer } from "../components/har-viewer";
import { LiveView } from "../components/live-view";
import { LogsViewer } from "../components/logs-viewer";
import { SessionFilesTab } from "../components/session-files-tab";
import { useApiMutation } from "../hooks/use-api-mutation";
import { useApiQuery } from "../hooks/use-api-query";
import { useTypedQueryParams } from "../hooks/use-query-params";
import { useSSE } from "../hooks/use-sse";
import { DBStatus, type Test, type TestRunLogs } from "../types";
import type {
  BrowserSession,
  BrowserSessionEvent,
  ConsoleLogsResponse,
} from "../types/browser-session";
import { formatDurationFromTimes } from "../utils";
import { clickableProps } from "../utils/keyboard";

interface DetailPageTabs {
  tab?: "console" | "network" | "files";
}

export default function BrowserSessionDetailsPage() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { params, setParam } = useTypedQueryParams<DetailPageTabs>();
  const [eventsAutoScroll, setEventsAutoScroll] = useState(true);
  const queryClient = useQueryClient();
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  // Fetch session details
  const { data: session, isLoading } = useApiQuery<BrowserSession>(
    `/browser-sessions/${sessionId}`,
    {
      enabled: !!sessionId,
      refetchInterval: (query) => {
        const data = query.state.data;
        // Poll more frequently for active sessions
        if (!["closed", "failed"].includes(data?.status ?? "active")) {
          return 2000;
        }
        return false;
      },
    },
  );

  const isActive = session?.status === "active" || session?.status === "starting";

  // Close session mutation
  const { mutate: closeSession, isPending: isClosing } = useApiMutation<
    void,
    { status: string; session_id: string }
  >("DELETE", `/browser-sessions/${sessionId}`, {
    onSuccess: (data) => {
      enqueueSnackbar(
        data.status === "already_closed" ? "Session was already closed" : "Session is closing...",
        { variant: data.status === "already_closed" ? "info" : "success" },
      );
      setCloseConfirmOpen(false);
      queryClient.invalidateQueries({
        queryKey: [`/browser-sessions/${sessionId}`],
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to close session: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // SSE for events - connect for all sessions to get history, then stream for active ones
  // Only connect once we have session data to avoid duplicate connections
  const { events: sseEvents, isConnected: sseConnected } = useSSE<BrowserSessionEvent>(
    session && sessionId ? `/browser-sessions/${sessionId}/events-stream` : "",
    {
      autoReconnect: isActive, // Only reconnect for active sessions
      pauseWhenHidden: true,
    },
  );

  // Tab state
  const tab = params.tab ?? "console";
  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      setParam("tab", newValue as DetailPageTabs["tab"]);
    },
    [setParam],
  );

  // Lazy load console logs (only when tab is selected)
  const { data: consoleLogs, isLoading: consoleLoading } = useApiQuery<ConsoleLogsResponse>(
    `/browser-sessions/${sessionId}/logs/console`,
    {
      enabled: !!sessionId && tab === "console",
    },
  );

  // Fetch setup test info if after_test_id is present
  const afterTestId = session?.config?.after_test_id;
  const { data: setupTest } = useApiQuery<Test>(`/tests/${afterTestId}`, {
    enabled: !!afterTestId,
  });

  // Duration calculation
  const duration = useMemo(() => {
    if (!session) return null;
    return formatDurationFromTimes(session.created_at, session.last_action_at, {
      showRunning: isActive,
    });
  }, [session, isActive]);

  // Copy session ID to clipboard
  const copySessionId = useCallback(() => {
    if (session?.id) {
      navigator.clipboard.writeText(session.id);
      enqueueSnackbar("Session ID copied to clipboard", {
        variant: "success",
        autoHideDuration: 2000,
      });
    }
  }, [session?.id, enqueueSnackbar]);

  if (isLoading) {
    return <LoadingBox sx={{ height: "50vh" }} />;
  }

  if (!session) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Session not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => nav("/browser-sessions")}
          sx={{ mt: 2 }}
        >
          Back to Sessions
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => nav("/browser-sessions")}
              size="small"
              variant="text"
            >
              Sessions
            </Button>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                ID:
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {session.id.slice(0, 8)}...
              </Typography>
              <Tooltip title="Copy full session ID">
                <IconButton aria-label="Copy full session ID" size="small" onClick={copySessionId}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <BrowserSessionStatusChip status={session.status} />
            {isActive && (
              <Tooltip title="Close Session">
                <IconButton
                  aria-label="Close Session"
                  size="small"
                  onClick={() => setCloseConfirmOpen(true)}
                  disabled={isClosing}
                  sx={{ color: "warning.main" }}
                >
                  <StopCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            divider={
              <Typography variant="body2" color="text.secondary">
                ·
              </Typography>
            }
          >
            <Tooltip title="Created at">
              <Typography variant="body2" color="text.secondary">
                {new Date(session.created_at).toLocaleString()}
              </Typography>
            </Tooltip>
            {duration && (
              <Typography variant="body2" color="text.secondary">
                {duration}
              </Typography>
            )}
            {session.current_url && (
              <Tooltip title={session.current_url}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {session.current_url}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <PanelGroup direction="horizontal">
          {/* Left Panel - Video/Screenshot + Logs Tabs */}
          <Panel defaultSize={70} minSize={40}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Video/Screenshot Area */}
              <Box sx={{ flex: 1, minHeight: 0, p: 2, overflow: "auto" }}>
                <VideoOrScreenshot session={session} />
              </Box>

              {/* Logs Tabs */}
              <Box sx={{ borderTop: 1, borderColor: "divider", height: "40%" }}>
                <TabContext value={tab}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <TabList onChange={handleTabChange} aria-label="Session logs tabs">
                      <Tab label="Console" value="console" />
                      <Tab label="Network" value="network" />
                      <Tab
                        label={`Files${(session.downloads?.length ?? 0) + (session.uploads?.length ?? 0) > 0 ? ` (${(session.downloads?.length ?? 0) + (session.uploads?.length ?? 0)})` : ""}`}
                        value="files"
                        disabled={isActive}
                      />
                    </TabList>
                  </Box>
                  <TabPanel
                    value="console"
                    sx={{
                      p: 0,
                      pt: 1,
                      px: 1,
                      height: "calc(100% - 48px)",
                      overflow: "auto",
                    }}
                  >
                    <ConsoleLogsPanel
                      logs={consoleLogs}
                      isLoading={consoleLoading}
                      isActive={false}
                      sessionId={sessionId ?? ""}
                    />
                  </TabPanel>
                  <TabPanel
                    value="network"
                    sx={{
                      p: 0,
                      pt: 1,
                      px: 1,
                      height: "calc(100% - 48px)",
                      overflow: "hidden",
                    }}
                  >
                    {isActive ? (
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <Alert severity="info">
                          Network logs are available after the session closes
                        </Alert>
                      </Box>
                    ) : session.har_url ? (
                      <Box sx={{ p: 1, height: "100%", overflow: "auto" }}>
                        <HarViewer harPath={session.har_url} />
                      </Box>
                    ) : (
                      <EmptyState message="No network data available" sx={{ p: 2 }} />
                    )}
                  </TabPanel>
                  <TabPanel
                    value="files"
                    sx={{
                      p: 0,
                      pt: 1,
                      px: 1,
                      height: "calc(100% - 48px)",
                      overflow: "auto",
                    }}
                  >
                    <SessionFilesTab downloads={session.downloads} uploads={session.uploads} />
                  </TabPanel>
                </TabContext>
              </Box>
            </Box>
          </Panel>

          <PanelResizeHandle>
            <Divider orientation="vertical" sx={{ mx: 0.5 }}>
              <MultipleStopIcon color="disabled" fontSize="small" />
            </Divider>
          </PanelResizeHandle>

          {/* Right Panel - Events Stream */}
          <Panel defaultSize={30} minSize={20}>
            <EventsPanel
              events={sseEvents}
              isConnected={sseConnected}
              isActive={isActive}
              autoScroll={eventsAutoScroll}
              onToggleAutoScroll={() => setEventsAutoScroll((prev) => !prev)}
              setupTest={setupTest}
            />
          </Panel>
        </PanelGroup>
      </Box>

      {/* Close Session Confirmation Dialog */}
      <Dialog open={closeConfirmOpen} onClose={() => setCloseConfirmOpen(false)}>
        <DialogTitle>Close Browser Session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to close this browser session? This will stop all browser
            activity.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => closeSession({})}
            color="warning"
            variant="contained"
            disabled={isClosing}
          >
            {isClosing ? "Closing..." : "Close Session"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function VideoOrScreenshot({ session }: { session: BrowserSession }) {
  const isActive = session.status === "active" || session.status === "starting";

  // For closed sessions with recording
  if (!isActive && session.recording_url) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <video
          controls
          autoPlay
          muted
          style={{
            width: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            borderRadius: "8px",
            backgroundColor: theme.palette.common.black,
          }}
        >
          <source src={session.recording_url} type="video/webm" />
          Your browser does not support the video tag.
        </video>
      </Box>
    );
  }

  // For active sessions - show LiveView component
  if (isActive) {
    return (
      <LiveView
        liveViewUrl={session.liveview_url ?? undefined}
        pageUrl={session.current_url ?? undefined}
        height="100%"
        noLiveViewMessage="Initializing browser session..."
      />
    );
  }

  // No recording available
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        backgroundColor: "grey.50",
        borderRadius: 2,
      }}
    >
      <Alert severity="info">No recording available for this session</Alert>
    </Box>
  );
}

function EventsPanel({
  events,
  isConnected,
  isActive,
  autoScroll,
  onToggleAutoScroll,
  setupTest,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events: Array<{ id: string; event: string; data: any }>;
  isConnected: boolean;
  isActive: boolean;
  autoScroll: boolean;
  onToggleAutoScroll: () => void;
  setupTest?: Test;
}) {
  // Sort events by timestamp (oldest first for display)
  // Events without timestamps go to the end
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aTime = a.data.timestamp
        ? new Date(a.data.timestamp).getTime()
        : Number.MAX_SAFE_INTEGER;
      const bTime = b.data.timestamp
        ? new Date(b.data.timestamp).getTime()
        : Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
  }, [events]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Events</Typography>
            {isActive && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: isConnected ? "success.main" : "warning.main",
                }}
              />
            )}
          </Stack>
          <Button size="small" variant="text" onClick={onToggleAutoScroll}>
            Auto-scroll: {autoScroll ? "ON" : "OFF"}
          </Button>
        </Stack>
      </Box>

      {/* Events List */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        <Stack spacing={0.5}>
          {/* Setup test event (always first if present) */}
          {setupTest && (
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                backgroundColor: "action.hover",
                borderLeft: 3,
                borderColor: "info.main",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ color: "info.main" }}>▶</Typography>
                <Typography variant="body2">
                  Setup test:{" "}
                  <Link
                    component={RouterLink}
                    to={`/tests-main/tests/${setupTest.id}`}
                    sx={{ fontWeight: 500 }}
                  >
                    {setupTest.name}
                  </Link>
                </Typography>
              </Stack>
            </Box>
          )}
          {sortedEvents.length === 0 && !setupTest ? (
            <EmptyState
              message={isActive ? "Waiting for events..." : "No events recorded"}
              sx={{ p: 2 }}
            />
          ) : (
            sortedEvents.map((evt) => <EventRow key={evt.id} event={evt} />)
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function EventRow({
  event,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: { id: string; event: string; data: any };
}) {
  const [expanded, setExpanded] = useState(false);

  // Handle missing or invalid timestamp
  const timestamp = event.data.timestamp;
  const time = timestamp ? new Date(timestamp).toLocaleTimeString() : "-";

  // Use event_type from data, or fall back to SSE event name
  const eventType = event.data.event_type ?? event.event;

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case "action_request":
        return "→";
      case "action_response":
        return "←";
      case "lifecycle":
      case "session_ended":
        return "●";
      default:
        return "○";
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case "action_request":
        return "primary.main";
      case "action_response":
        return "success.main";
      case "lifecycle":
        return "warning.main";
      default:
        return "text.secondary";
    }
  };

  return (
    <Box
      {...clickableProps(() => setExpanded((prev) => !prev))}
      onClick={() => setExpanded((prev) => !prev)}
      sx={{
        p: 1,
        borderRadius: 1,
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.hover" },
        backgroundColor: expanded ? "action.selected" : "transparent",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
          {time}
        </Typography>
        <Typography sx={{ color: getEventColor(eventType) }}>{getEventIcon(eventType)}</Typography>
        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
          {eventType}
        </Typography>
      </Stack>
      {expanded && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: "grey.100",
            borderRadius: 1,
            overflow: "auto",
          }}
        >
          <Typography
            variant="caption"
            component="pre"
            sx={{
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {JSON.stringify(event.data.event_data ?? event.data, null, 2)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ConsoleLogsPanel({
  logs,
  isLoading,
  isActive,
  sessionId,
}: {
  logs: ConsoleLogsResponse | undefined;
  isLoading: boolean;
  isActive: boolean;
  sessionId: string;
}) {
  // Transform ConsoleLogEntry to TestRunLogs format for LogsViewer
  const transformedLogs: TestRunLogs[] = useMemo(() => {
    if (!logs?.logs) return [];
    return logs.logs.map((log, idx) => ({
      id: `${sessionId}-log-${idx}`,
      test_id: sessionId,
      test_run_id: sessionId,
      text: log.text,
      type: log.level,
      created_at: log.timestamp,
      updated_at: log.timestamp,
      created_by: "",
      status: DBStatus.ACTIVE,
    }));
  }, [logs, sessionId]);

  if (isActive) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <EmptyState variant="alert" message="Console logs are available after the session closes" />
      </Box>
    );
  }

  if (isLoading) {
    return <LoadingBox size={24} sx={{ p: 2 }} />;
  }

  if (!logs || logs.logs.length === 0) {
    return <EmptyState message="No console logs" sx={{ p: 2 }} />;
  }

  return (
    <Box sx={{ p: 1, height: "100%", overflow: "auto" }}>
      <LogsViewer logs={transformedLogs} />
    </Box>
  );
}
